import { useCallback, useMemo, useState } from 'react'
import {
  FiCreditCard,
  FiDollarSign,
  FiEye,
  FiPackage,
  FiPercent,
  FiShoppingBag,
  FiShoppingCart
} from 'react-icons/fi'
import { IoMdPerson } from 'react-icons/io'
import { MdAdsClick } from 'react-icons/md'
import { useParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import CardSelector from '~/components/CardSelector'
import Loading from '~/components/Loading'
import { formatDateToString } from '~/helpers'
import { handleErrorMessage } from '~/helpers/errors'
import useQueryParams from '~/hooks/useQuery'
import Card from '~/modules/retailMedia/components/Card'
import { WrapperCard } from '~/modules/retailMedia/components/Card/styles'
import { parseLookbackMonths } from '~/modules/retailMedia/constants'
import { formatIndustryReportCampaign } from '~/modules/retailMedia/dtos/industry/campaign/report'
import {
  getCampaignsReportKey,
  getCampaignsReportService
} from '~/modules/retailMedia/services/report'
import { useAppSelector } from '~/store/hooks'

import { useCards } from './useCards'

import { CardsGrid, CardsWrapper } from './styles'

const CardsCampaign: React.FC = ({
  strategy,
  adType,
  campaignAdType,
  target
}: MetricsProps) => {
  const { id } = useParams()
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false)

  const { startDate, endDate, compareStartDate, compareEndDate } =
    useAppSelector(state => state.datepickerHeader)
  const hasCompareInterval = !!compareStartDate && !!compareEndDate

  const campaignPublisherId = useAppSelector(
    state => state.retailMedia.campaign.item.data?.publisher?.id
  )

  // gráfico, cards e listagem desta tela leem os mesmos filtros da URL
  const searchParams = useQueryParams()
  const product_sku = searchParams.get('product_sku')
  const ad_status = searchParams.get('ad_status') as RetailMediaAdsStatuses
  const lookbackMonths = parseLookbackMonths(
    searchParams.get('lookback_months')
  )

  const queryParams = useMemo(
    () => ({
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
      campaign_id: id,
      ad_status,
      product_sku: adType === 'product' ? product_sku : undefined,
      ad_type: adType,
      target,
      lookback_months: lookbackMonths
    }),
    [
      startDate,
      endDate,
      id,
      ad_status,
      product_sku,
      adType,
      target,
      lookbackMonths
    ]
  )

  const queryParamsCompare = useMemo(
    () => ({
      start_date: formatDateToString(compareStartDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(compareEndDate, 'YYYY-MM-DD'),
      campaign_id: id,
      ad_status,
      product_sku: adType === 'product' ? product_sku : undefined,
      ad_type: adType,
      target,
      lookback_months: lookbackMonths
    }),
    [
      compareStartDate,
      compareEndDate,
      id,
      ad_status,
      product_sku,
      adType,
      target,
      lookbackMonths
    ]
  )

  const {
    data,
    isLoading: loading,
    error
  } = useQuery({
    queryKey: [getCampaignsReportKey, id, ...Object.values(queryParams)],
    queryFn: async () => {
      const { data } = await getCampaignsReportService(queryParams)

      return formatIndustryReportCampaign(data)
    }
  })

  const { data: compareData } = useQuery({
    queryKey: [
      getCampaignsReportKey,
      'compare',
      id,
      ...Object.values(queryParamsCompare)
    ],
    queryFn: async () => {
      const { data } = await getCampaignsReportService(queryParamsCompare)

      return formatIndustryReportCampaign(data)
    },
    enabled: hasCompareInterval
  })

  const {
    cardsToShow,
    cardsToCustomize,
    hiddenCards,
    defaultCardsToCustomize,
    defaultHiddenCards,
    saveCustomization,
    resetCustomization
  } = useCards({
    data,
    compareData,
    strategy,
    adType,
    campaignAdType,
    campaignPublisherId,
    endDate,
    lookbackMonths
  })

  const icons: Record<AvailableCards, JSX.Element> = {
    impressions: <FiEye className=" text-warning" />,
    views: <FiEye className=" text-warning" />,
    clicks: <MdAdsClick className=" text-info" />,
    conversions: <FiShoppingBag className=" text-success" />,
    conversionRate: <FiPercent className=" text-primary" />,
    ctr: <FiPercent className=" text-primary" />,
    ecpm: <FiDollarSign className=" text-info" />,
    adCost: <FiPercent className=" text-primary" />,
    income: <FiDollarSign className=" text-success" />,
    roas: <FiShoppingBag className=" text-success" />,
    totalSpent: <FiCreditCard className=" text-warning" />,
    roasClick: <MdAdsClick className=" text-info" />,
    roasView: <FiEye className=" text-warning" />,
    haloRevenue: <FiDollarSign className=" text-success" />,
    roasHalo: <FiShoppingBag className=" text-success" />,
    haloOrders: <FiShoppingCart className=" text-info" />,
    haloItems: <FiPackage className=" text-info" />,
    roasOverall: <FiShoppingBag className=" text-success" />,
    impressionShare: <FiPercent className=" text-primary" />,
    lostIsRank: <FiPercent className=" text-primary" />,
    lostIsBudget: <FiPercent className=" text-primary" />,
    newToBrandCustomers: <IoMdPerson className=" text-warning" />,
    newToBrandCustomersPercent: <FiPercent className=" text-warning" />,
    newToBrandRevenue: <FiDollarSign className=" text-success" />,
    newToBrandRevenuePercent: <FiPercent className=" text-success" />
  }

  const handleOpenCustomizeCards = useCallback(() => {
    setIsCustomizeOpen(true)
  }, [])
  const handleCloseCustomizeCards = useCallback((callback?: () => void) => {
    callback?.()
    setIsCustomizeOpen(false)
  }, [])

  return (
    <CardsWrapper>
      <Loading status={loading}>{t('rm:metrics.cards.loading')}</Loading>

      {!!error && (
        <Alert variant="critical">
          {t('rm:error')}:{' '}
          {handleErrorMessage({
            error,
            fallbackMessage: t('rm:metrics.cards.loading.fallbackMessage')
          })}
        </Alert>
      )}

      <WrapperCard>
        <CardsGrid>
          {cardsToShow.map(el => (
            <Card
              key={el.type}
              title={el.title}
              value={el.value}
              loading={loading}
              icon={icons?.[el.type]}
              isCompare={hasCompareInterval}
              badgeOverride={el.badge}
              compareValue={el.compareValue}
              compareInterval={{
                startDate: compareStartDate,
                endDate: compareEndDate
              }}
            />
          ))}
        </CardsGrid>

        <div className="d-flex justify-content-end mt-3">
          <CardSelector.ToggleButton onClick={handleOpenCustomizeCards} />
        </div>
      </WrapperCard>

      <CardSelector.Drawer
        cards={cardsToCustomize}
        hiddenCards={hiddenCards}
        defaultCardsToCustomize={defaultCardsToCustomize}
        defaultHiddenCards={defaultHiddenCards}
        isOpen={isCustomizeOpen}
        onClose={handleCloseCustomizeCards}
        onChange={saveCustomization}
        onReset={resetCustomization}
      />
    </CardsWrapper>
  )
}

export default CardsCampaign
